import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { ModuleDataValue } from "../hooks/types";
import { useSchemaIntegrationData } from "../hooks/useSchemaIntegrationData";
import type { BaseComponentProps } from "../types";
import { emptyDisplay } from "../utils/cellRenderers";
import { getIcon } from "../utils/iconMap";

import { Badge } from "@/components/ui/v2/badge";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import type {
  MetricCardColumns,
  MetricCardDefinition,
  MetricCardsProps,
} from "@/features/integrations/types";
import { cn } from "@/lib/utils/cn";
import { useValueFormatter } from "@/utils/useValueFormatter";
import type { ValueFormat } from "@/utils/valueFormat";

type MetricCardsComponentProps = BaseComponentProps & MetricCardsProps;

const DEFAULT_COLUMNS: MetricCardColumns = 3;

const COLUMN_CLASSES: Record<MetricCardColumns, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4",
  5: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
  6: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
};

function formatDisplay(
  value: ModuleDataValue,
  format: ValueFormat | undefined,
  formatValue: (value: number | null | undefined, format: ValueFormat) => string,
): string {
  if (value == null || typeof value === "number") {
    return formatValue(value, format ?? "whole");
  }
  return String(value);
}

function resolveDisplayValue(
  rawValue: ModuleDataValue,
  card: MetricCardDefinition,
  formatValue: (value: number | null | undefined, format: ValueFormat) => string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const isNull = rawValue === null || rawValue === undefined;

  if (isNull && card.showNullAs) {
    return emptyDisplay(card.showNullAs, undefined, t)!;
  }

  if (rawValue === 0) {
    const zeroDisplay = emptyDisplay(card.showZeroAs, undefined, t);
    if (zeroDisplay !== undefined) {
      return zeroDisplay;
    }
  }

  return formatDisplay(rawValue, card.format, formatValue);
}

function resolveSubtitle(
  card: MetricCardDefinition,
  effectiveData: Record<string, unknown> | undefined,
  formatValue: (value: number | null | undefined, format: ValueFormat) => string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  const subtitleValue = card.subtitleDataKey
    ? effectiveData?.[card.subtitleDataKey]
    : undefined;

  if (subtitleValue == null) {
    if (card.subtitle) return t(card.subtitle);
    return undefined;
  }

  let formattedSubtitleValue: string;
  if (card.subtitleFormat != null && typeof subtitleValue === "number") {
    formattedSubtitleValue = formatValue(subtitleValue, card.subtitleFormat);
  } else {
    formattedSubtitleValue = String(subtitleValue);
  }

  if (!card.subtitle) return formattedSubtitleValue;

  return t(card.subtitle, {
    count: typeof subtitleValue === "number" ? subtitleValue : undefined,
    formattedCount: formattedSubtitleValue,
  });
}

function renderBadge(
  card: MetricCardDefinition,
  data: Record<string, unknown> | undefined,
  t: (key: string) => string,
) {
  if (!card.badge) return null;
  if (!data?.[card.badge.dataKey]) return null;

  return (
    <Badge variant={card.badge.variant === "Warning" ? "warning" : "default"}>
      {t(card.badge.trueLabel)}
    </Badge>
  );
}

export function MetricCardsComponent({ cards, columns = DEFAULT_COLUMNS, data, integrationName, moduleName, clientId, tenantId }: MetricCardsComponentProps) {
  const moduleData = useSchemaIntegrationData(integrationName, moduleName ?? "", clientId, tenantId);
  const effectiveData = moduleData?.data ?? data;
  const { t } = useTranslation();
  const { format: formatValue } = useValueFormatter();

  return (
    <div className={cn("grid gap-4 pb-8", COLUMN_CLASSES[columns] ?? COLUMN_CLASSES[DEFAULT_COLUMNS])}>
      {cards.filter((card) => {
        if (!card.hideWhenNull) return true;
        const val = effectiveData?.[card.dataKey];
        return val !== null && val !== undefined;
      }).map((card) => {
        const rawValue = effectiveData?.[card.dataKey];
        const displayValue = resolveDisplayValue(rawValue, card, formatValue, t);

        const Icon = getIcon(card.icon);
        const iconElement = Icon ? <Icon className="size-5 text-primary" /> : null;

        const badgeElement = renderBadge(card, effectiveData, t);

        const value: ReactNode = badgeElement ? (
          <span className="flex items-center gap-2">
            {displayValue}
            {badgeElement}
          </span>
        ) : (
          displayValue
        );

        const subtitle = resolveSubtitle(card, effectiveData, formatValue, t);

        return (
          <MetricCard
            key={card.dataKey}
            variant="gradient"
            icon={iconElement}
            label={t(card.label)}
            value={value}
            subtitle={subtitle}
          />
        );
      })}
    </div>
  );
}
